import * as React from "react";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolved: "light" | "dark";
}

const KEY = "sc.theme";
const Ctx = React.createContext<ThemeContextValue | null>(null);

/**
 * v1: визуально только тёмная тема (UI переключателя выключен).
 * Логика на месте — когда добавим светлую палитру, просто отрендерим переключатель.
 */
export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}): JSX.Element {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
    return (saved as Theme | null) ?? defaultTheme;
  });

  const resolved: "light" | "dark" = React.useMemo(() => {
    if (theme === "system") {
      if (typeof window === "undefined") return "dark";
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme;
  }, [theme]);

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
  }, [resolved]);

  const setTheme = React.useCallback((t: Theme) => {
    localStorage.setItem(KEY, t);
    setThemeState(t);
  }, []);

  return (
    <Ctx.Provider value={{ theme, setTheme, resolved }}>{children}</Ctx.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useTheme must be used within ThemeProvider");
  return v;
}
